import { Fragment } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { MollieConnection, MollieProfile } from '../../Shared/types';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import { Select } from '../../Shared/UI/Select';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useRouter } from '../router';

export default function MollieProfileForm({
  mollieConnections,
}: {
  mollieConnections: MollieConnection[]
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const mollieProfiles = mollieConnections.reduce((mollieProfiles, mollieConnection) => ([
    ...mollieProfiles,
    ...mollieConnection.profiles,
  ]), [] as MollieProfile[]);

  const defaultProfile = mollieProfiles.filter((mollieProfile) => mollieProfile.default)[0];

  const { register, handleSubmit, watch, formState: { errors, defaultValues }, setError } = useForm({
    defaultValues: {
      mollie_profile_id: defaultProfile?.id,
    },
  });

  const selectedMollieProfileId = watch('mollie_profile_id');
  const selectedMollieProfile = mollieProfiles.filter((mollieProfile) => mollieProfile.id === selectedMollieProfileId)[0];

  const [
    setDefaultMollieProfile,
    { loading, errors: serverErrors },
  ] = useMutation(routes.set_default_mollie_profile(selectedMollieProfileId), {
    onSuccess: () => {
      notifier.notify(t('backend:updated_default_website_profile'));
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  return (
    <form onSubmit={handleSubmit(setDefaultMollieProfile)} className="space-y-6">
      <Card panels>
        <div className="space-y-6">
          {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} />}

          <InputGroup>
            <InputLabel required valid={!errors.mollie_profile_id} htmlFor="default_mollie_profile">
              {t('backend:website_profile')}
            </InputLabel>
            <InputDescription>
              {t('backend:website_profile_description')}
            </InputDescription>
            <Select {...register('mollie_profile_id', { required: true })} id="default_mollie_profile">
              {mollieConnections.map((connection) => (
                <Fragment key={connection.id}>
                  {connection.profiles.length > 0 && (
                    <optgroup label={connection.title || connection.id}>
                      {connection.profiles.map((profile) => (
                        <option value={profile.id} key={profile.id}>
                          {profile.title}
                          {' • '}
                          {profile.website}
                          {!profile.verified && ` (${t('backend:unverified')})`}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </Fragment>
              ))}
            </Select>
            <ErrorMessage attribute={t('backend:website_profile')} error={errors.mollie_profile_id }/>
            {selectedMollieProfile && selectedMollieProfile.payment_methods.length > 0 && (
              <div className="mt-2">
                {`${t('backend:payment_methods')}: `}
                {selectedMollieProfile.payment_methods.map((paymentMethod, index) => (
                  <Fragment key={paymentMethod.id}>
                    {index > 0 && ', '}
                    {t(`shared:payment_method.${paymentMethod.id}`)}
                  </Fragment>
                ))}
              </div>
            )}
            {selectedMollieProfile && selectedMollieProfile.payment_methods.length === 0 && (
              <div className="mt-2 text-red-600">
                {t('backend:no_payment_methods_activated')}
              </div>
            )}
          </InputGroup>
        </div>
        <div className="!py-4 border-slate-100 bg-slate-50 text-right">
          <Button
            type="submit"
            disabled={loading}
            loading={loading}
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}
